/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React from 'react'
import { defineMessages } from 'react-intl'
import { Navigate, Outlet, type RouteObject } from 'react-router-dom'

import Feature from '@modules/utils/feature'
import getProjectRoutes from '@modules/project-user-pages/routes'
import getOnboardingRoutes from '@modules/onboarding-combine-flow/routes'
import { getCreateResourceRoutes } from '@modules/project-creation-lib/routes'
import PricingPage from '@modules/pricing-list-components/PricingPage'
import OpenKibanaRedirectPage from '@modules/kibana-redirect/OpenKibanaRedirectPage'
import { MfaEnforcement } from '@modules/mfa-enforcement/MfaEnforcement'
import RoutablePage from '@modules/routing/RoutablePage'
import getCCMServiceSelectionRoutes from '@modules/ccm-user-lib/routes'

import { withLDWrapper } from '@/apps/userconsole/components/LaunchdarklyWrapper'
import ManageAccountTrustRelationship from '@/components/DeploymentTrustRelationships/ManageAccountTrustRelationship'
import CreateDeploymentTrustRelationship from '@/components/DeploymentTrustRelationships/CreateDeploymentTrustRelationship'
import ManageDirectTrustRelationship from '@/components/DeploymentTrustRelationships/ManageDirectTrustRelationship'
import {
  getSupportedSliderInstanceTypesWithoutEs,
  getSupportedSliderInstanceTypes,
} from '@/lib/sliders/support'
import { getSliderPrettyName } from '@/lib/sliders/messages'
import { isFedRampHigh } from '@/lib/fedramp-high'
import EditUserAuthentication from '@/components/Security/EditUserAuthentication'
import CreateOrEditRoleMapping from '@/components/Security/CreateOrEditRoleMapping'
import BillingFeedback from '@/components/Billing/BillingFeedback'
import RedirectToDeploymentResource from '@/components/RedirectToDeploymentResource'

import APIConsole from '../../components/APIConsole'
import ClusterSnapshots from '../../components/Cluster/Snapshots'
import Account from '../../components/Portal/components/Account'
import Billing from '../../components/Portal/components/Billing'
import PortalLandingPage from '../../components/Portal/components/PortalLandingPage'
import SnapshotDetails from '../../components/Cluster/Snapshots/SnapshotDetails'
import Deployment from '../../components/Deployment'
import DeploymentOverview from '../../components/Deployment/Overview'
import HealthStatus from '../../components/Deployment/HealthStatus'
import HealthStatusDetails from '../../components/Deployment/HealthStatusDetails'
import DeploymentGettingStarted from '../../components/Deployment/DeploymentGettingStarted'
import UserAccountSettings from '../../components/Portal/components/UserAccountSettings'
import UserRegistration from '../../components/UserRegistration'
import ServerlessUserRegistration from '../../components/UserRegistration/ServerlessUserRegistration'
import ManageTrafficFilters from '../../components/ManageTrafficFilters'
import CreateStackDeploymentRoute from '../../components/StackDeploymentEditor/CreateStackDeploymentRoute'
import EditStackDeployment from '../../components/StackDeploymentEditor/EditStackDeployment'
import StackDeploymentsRoute from '../../components/StackDeploymentsRoute'
import StackDeploymentActivity from '../../components/StackDeploymentActivity'
import StackDeploymentSlider from '../../components/StackDeploymentSlider'
import Logout from '../../components/Logout'
import NotFound from '../../components/ErrorPages/NotFound'
import AccessDenied from '../../components/ErrorPages/AccessDenied'
import SsoErrors from '../../components/ErrorPages/SsoErrors'
import IlmMigration from '../../components/IlmMigration'
import DeploymentExtensions from '../../components/DeploymentExtensions'
import DeploymentExtensionsGate from '../../components/DeploymentExtensionsGate'
import DeploymentExtensionCreate from '../../components/DeploymentExtensionCreate'
import DeploymentExtensionEdit from '../../components/DeploymentExtensionEdit'
import OAuthLogin from '../../components/Login/OAuthLogin'
import AzureLogin from '../../components/Login/AzureLogin'
import UserconsoleSso from '../../components/Sso'
import { isFeatureActivated } from '../../selectors'
import getNetworkSecurityRoutes from '../../../modules/network-security-pages/routes'

import AppRoot from './components/AppRoot'
import AuthenticatedPageWrapper from './components/AuthenticatedPageWrapper'
import DeploymentFeatures from './components/DeploymentFeatures'
import ClusterOverview from './components/Cluster/Overview'
import LogsMetrics from './components/Deployment/Logs'
import TrustManagement from './components/TrustManagement'
import ConfirmEmailChange from './components/ConfirmEmailChange'
import FullStoryCookieNotice from './components/FullStoryCookieNotice'
import Tracking from './components/Tracking'
import ForgotPassword from './components/ForgotPassword'
import UserconsoleLogin from './components/Login'
import Support from './components/Support'
import PerformanceMetrics from './components/PerformanceMetrics'
import StackDeploymentPricingRoute from './components/StackDeploymentPricingRoute'
import ResetPassword from './components/ResetPassword'
import CreatePassword from './components/CreatePassword'
import AcceptInvitation from './components/AcceptInvitation'
import MarketplaceSignupModalV2 from './components/PartnerSignup/AWSMarketplaceSignupModal/MarketplaceSignupModalV2'
import PartnerSignupLogin from './components/PartnerSignup/GCPMarketplaceSignup/PartnerSignupLogin'
import MarketplaceConversion from './components/PartnerSignup/MarketplaceConversion'
import DeploymentSecurity from './components/DeploymentSecurity'
import MarketplaceRegistrationRedirect from './components/PartnerSignup/AWSMarketplaceSignupModal/MarketplaceRegistrationRedirect'
import PartnerSignupRegistration from './components/PartnerSignup/GCPMarketplaceSignup/PartnerSignupRegistration'
import AWSPrepaidLandingPage from './components/PartnerSignup/AWSMarketplaceSignupModal/AWSPrepaidLandingPage'

import type { ReduxState } from '@/types/redux'
import type { IntlShape } from 'react-intl'

const messages = defineMessages({
  deploymentActivityPageSlider: {
    id: `userconsole-route-titles.deployment-activity-page-slider`,
    defaultMessage: `{name} Activity`,
  },
  sliderManagePageTitle: {
    id: `userconsole-route-titles.deployment-manage-page-slider`,
    defaultMessage: `Manage {name}`,
  },
  userAuthentication: {
    id: `page-title.organization.security.user-authentication`,
    defaultMessage: `User authentication`,
  },
  roleMapping: {
    id: `page-title.organization.security.role-mapping`,
    defaultMessage: `Role mapping`,
  },
})

const RoutableUserconsoleLogin = withLDWrapper(UserconsoleLogin)
const RoutableMarketplaceSignupModalV2 = withLDWrapper(MarketplaceSignupModalV2)
const RoutableUserRegistration = withLDWrapper(UserRegistration)
const RoutableServerlessUserRegistration = withLDWrapper(ServerlessUserRegistration)
const RoutablePartnerSignupRegistration = withLDWrapper(PartnerSignupRegistration)
const RoutableResetPassword = withLDWrapper(ResetPassword)
const RoutableAcceptInvitation = withLDWrapper(AcceptInvitation)
const RoutableForgotPassword = withLDWrapper(ForgotPassword)
const RoutableAWSPrepaidLandingPage = withLDWrapper(AWSPrepaidLandingPage)

export function getRoutes({ intl, state }: { state: ReduxState; intl: IntlShape }): RouteObject[] {
  const { formatMessage } = intl

  const showPrices = isFeatureActivated(state, Feature.showPrices)
  const isServerlessEnabled = isFeatureActivated(state, Feature.serverless)
  const isCCMFeatureActivated = isFeatureActivated(state, Feature.ccm)

  const routes: RouteObject[] = [
    {
      element: <AppRoot />,
      children: [
        ...getUntrackedRoutes(),
        {
          element: <Tracking />,
          children: [
            ...getAnonymousRoutes(),
            {
              element: <AuthenticatedPageWrapper />,
              children: [
                ...getPortalRoutes(),
                ...getProjectRoutes(),
                ...getOnboardingRoutes(isCCMFeatureActivated),
                ...getCreateResourceRoutes(isServerlessEnabled),
                ...getStackDeploymentRoutes(),
                ...getDeploymentFeaturesRoutes(),
                ...getCCMServiceSelectionRoutes(),
                {
                  path: 'support',
                  element: (
                    <RoutablePage title='Support'>
                      <Support />
                    </RoutablePage>
                  ),
                },
              ],
            },
          ],
        },
        {
          path: '*',
          element: (
            <RoutablePage title='Not found'>
              <NotFound />
            </RoutablePage>
          ),
        },
      ],
    },
  ]

  return routes

  function getUntrackedRoutes(): RouteObject[] {
    return [
      {
        path: 'login',
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Login'>
                <RoutableUserconsoleLogin />
              </RoutablePage>
            ),
          },
          {
            path: 'oauth',
            element: (
              <RoutablePage title='Login'>
                <OAuthLogin />
              </RoutablePage>
            ),
          },
          {
            path: 'azure',
            element: (
              <RoutablePage title='Login'>
                <AzureLogin />
              </RoutablePage>
            ),
          },
          {
            path: 'marketplace',
            element: (
              <RoutablePage title='Marketplace Login'>
                <PartnerSignupLogin />
              </RoutablePage>
            ),
          },
          {
            path: 'sso/:loginIdentifier?',
            element: (
              <RoutablePage title='Login'>
                <UserconsoleSso />
              </RoutablePage>
            ),
          },
        ],
      },
      {
        path: 'logout',
        element: (
          <RoutablePage title='Logging out'>
            <Logout />
          </RoutablePage>
        ),
      },
      {
        path: '/marketplace/aws/prepaid-subscription',
        element: (
          <RoutablePage title='AWS prepaid subscription'>
            <RoutableAWSPrepaidLandingPage />
          </RoutablePage>
        ),
      },
    ]
  }

  function getAnonymousRoutes(): RouteObject[] {
    const registrationRoutes = [
      {
        path: 'registration',
        element: (
          <RoutablePage title='Create your account'>
            <RoutableUserRegistration />
          </RoutablePage>
        ),
      },
      {
        path: 'serverless-registration',
        element: (
          <RoutablePage title='Create your account'>
            <RoutableServerlessUserRegistration />
          </RoutablePage>
        ),
      },
      {
        path: 'partner-signup',
        element: (
          <RoutablePage title='Sign up'>
            <MarketplaceRegistrationRedirect />
          </RoutablePage>
        ),
      },
      {
        path: 'marketplace/registration/v2',
        element: (
          <RoutablePage title='Elastic Sign up'>
            <RoutableMarketplaceSignupModalV2 />
          </RoutablePage>
        ),
      },
      {
        path: 'marketplace-registration',
        element: (
          <RoutablePage title='Sign up'>
            <RoutablePartnerSignupRegistration />
          </RoutablePage>
        ),
      },
    ]
    return [
      ...(isFedRampHigh() ? [] : registrationRoutes),
      {
        path: 'account/reset-password',
        element: (
          <RoutablePage title='Reset your password'>
            <RoutableResetPassword />
          </RoutablePage>
        ),
      },
      {
        path: 'account/verify-email',
        element: (
          <RoutablePage title='Activate your account'>
            <CreatePassword />
          </RoutablePage>
        ),
      },
      {
        path: 'account/accept-invite',
        element: (
          <RoutablePage title='Accept email invitation'>
            <RoutableAcceptInvitation />
          </RoutablePage>
        ),
      },
      {
        path: 'forgot',
        element: (
          <RoutablePage title='Forgot password'>
            <RoutableForgotPassword />
          </RoutablePage>
        ),
      },
      {
        path: 'change-email',
        element: (
          <RoutablePage title='Confirm email change'>
            <ConfirmEmailChange />
          </RoutablePage>
        ),
      },
      {
        path: 'open-kibana/:resourceType/:id',
        element: (
          <RoutablePage title='Opening Kibana'>
            <OpenKibanaRedirectPage />
          </RoutablePage>
        ),
      },
      {
        path: 'cloud-pricing-table',
        element: (
          <RoutablePage title='Elastic Cloud Pricing Table'>
            <PricingPage />
          </RoutablePage>
        ),
      },
      {
        path: 'deployment-pricing-table',
        element: (
          <RoutablePage title='Elastic Cloud Pricing Table'>
            <PricingPage />
          </RoutablePage>
        ),
      },
      {
        path: 'errors/sso',
        element: (
          <RoutablePage title='Error'>
            <SsoErrors />
          </RoutablePage>
        ),
      },
      {
        path: 'access-denied',
        element: (
          <RoutablePage title='Access Denied'>
            <AccessDenied />
          </RoutablePage>
        ),
      },
      ...(showPrices
        ? [
            {
              path: 'pricing',
              element: (
                <RoutablePage title='Elasticsearch Service pricing calculator'>
                  <FullStoryCookieNotice>
                    <StackDeploymentPricingRoute />
                  </FullStoryCookieNotice>
                </RoutablePage>
              ),
            },
          ]
        : []),
    ]
  }

  function getDeploymentFeaturesRoutes(): RouteObject[] {
    const networkSecurityRoutes = getNetworkSecurityRoutes()

    return [
      {
        path: 'access-security',
        children: [
          {
            index: true,
            element:
              networkSecurityRoutes.length > 0 ? (
                <Navigate to='/access-security/network-security' />
              ) : (
                <RoutablePage title='Access and security'>
                  <DeploymentFeatures />
                </RoutablePage>
              ),
          },

          ...networkSecurityRoutes,

          ...(isFeatureActivated(state, Feature.trafficFiltering)
            ? [
                {
                  path: 'traffic-filters',
                  element: (
                    <RoutablePage title='Traffic filters'>
                      <ManageTrafficFilters />
                    </RoutablePage>
                  ),
                },
              ]
            : []),

          ...(isFeatureActivated(state, Feature.crossEnvCCSCCR)
            ? [
                {
                  path: 'trust-management',
                  element: (
                    <RoutablePage title='Trust management'>
                      <TrustManagement />
                    </RoutablePage>
                  ),
                },
              ]
            : []),
        ],
      },
      {
        path: 'extensions',
        element: (
          <DeploymentExtensionsGate>
            <Outlet />
          </DeploymentExtensionsGate>
        ),
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Deployment extensions'>
                <DeploymentExtensions />
              </RoutablePage>
            ),
          },
          {
            path: 'create',
            element: (
              <RoutablePage title='Create deployment extension'>
                <DeploymentExtensionCreate />
              </RoutablePage>
            ),
          },
          {
            path: ':extensionId/edit',
            element: (
              <RoutablePage title='Deployment extension (:extensionId)'>
                <DeploymentExtensionEdit />
              </RoutablePage>
            ),
          },
        ],
      },
    ]
  }

  function getPortalRoutes(): RouteObject[] {
    return [
      {
        path: 'home',
        element: (
          <RoutablePage>
            <PortalLandingPage />
          </RoutablePage>
        ),
      },
      {
        path: 'user/settings',
        element: (
          <RoutablePage title='User Settings'>
            <UserAccountSettings />
          </RoutablePage>
        ),
      },
      {
        path: 'account',
        children: [
          {
            path: 'contacts',
            element: (
              <RoutablePage title='Contacts'>
                <Account tabId='contacts' />
              </RoutablePage>
            ),
          },
          {
            path: 'keys',
            element: (
              <RoutablePage title='API keys'>
                <Account tabId='keys' />
              </RoutablePage>
            ),
          },
          {
            path: 'idp',
            element: (
              <RoutablePage title='Security'>
                <Account tabId='idp' />
              </RoutablePage>
            ),
          },
          {
            path: 'idp/user-authentication',
            element: (
              <RoutablePage title='User authentication'>
                <EditUserAuthentication />
              </RoutablePage>
            ),
          },
          {
            path: 'idp/role-mapping',
            element: (
              <RoutablePage title='Role mapping'>
                <CreateOrEditRoleMapping />
              </RoutablePage>
            ),
          },
          {
            path: 'idp/role-mapping/:roleMappingName',
            element: (
              <RoutablePage title='Role mapping'>
                <CreateOrEditRoleMapping />
              </RoutablePage>
            ),
          },
          {
            path: 'members',
            element: (
              <RoutablePage title='Members'>
                <Account selectedTabId='members' />
              </RoutablePage>
            ),
          },
        ],
      },
      {
        path: 'billing',
        children: [
          {
            path: 'overview',
            element: (
              <RoutablePage title='Overview'>
                <Billing tabId='overview' />
              </RoutablePage>
            ),
          },
          {
            path: 'usage',
            element: (
              <RoutablePage title='Usage'>
                <Billing tabId='usage' />
              </RoutablePage>
            ),
          },
          {
            path: 'history',
            element: (
              <RoutablePage title='History'>
                <Billing tabId='history' />
              </RoutablePage>
            ),
          },
          {
            path: 'subscription',
            element: (
              <RoutablePage title='Subscription'>
                <Billing tabId='subscription' />
              </RoutablePage>
            ),
          },
          {
            path: 'feedback',
            element: (
              <RoutablePage title='Feedback'>
                <BillingFeedback />
              </RoutablePage>
            ),
          },
        ],
      },
      {
        path: 'marketplace-conversion',
        element: (
          <RoutablePage title='Marketplace conversion'>
            <MarketplaceConversion />
          </RoutablePage>
        ),
      },
      {
        path: 'set-up-mfa',
        element: (
          <RoutablePage title='Please set up at least one multifactor'>
            <MfaEnforcement />
          </RoutablePage>
        ),
      },
    ]
  }

  function getStackDeploymentRoutes(): RouteObject[] {
    return [
      {
        path: 'deployments/resolve/:resourceType/:regionId/:resourceId',
        element: <RedirectToDeploymentResource />,
      },
      {
        path: 'deployments/resolve/:resourceType/:regionId/:resourceId/*',
        element: <RedirectToDeploymentResource />,
      },
      {
        path: 'deployments',
        children: [
          {
            index: true,
            element: (
              <RoutablePage title='Deployments'>
                <StackDeploymentsRoute />
              </RoutablePage>
            ),
          },
          {
            path: 'create',
            element: (
              <RoutablePage title='Create Deployment'>
                <CreateStackDeploymentRoute />
              </RoutablePage>
            ),
          },
          {
            path: ':deploymentId',
            element: <Deployment />,
            children: [
              {
                index: true,
                element: (
                  <RoutablePage title='Overview'>
                    <DeploymentOverview />
                  </RoutablePage>
                ),
              },
              {
                path: 'getting-started',
                element: (
                  <RoutablePage title='Getting started'>
                    <DeploymentGettingStarted />
                  </RoutablePage>
                ),
              },
              {
                path: 'edit',
                element: (
                  <RoutablePage title='Edit deployment'>
                    <EditStackDeployment />
                  </RoutablePage>
                ),
              },
              {
                path: 'health',
                element: (
                  <RoutablePage title='Monitoring'>
                    <HealthStatus />
                  </RoutablePage>
                ),
              },
              {
                path: 'health/:issue',
                element: (
                  <RoutablePage title='Health status'>
                    <HealthStatusDetails />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch',
                element: (
                  <RoutablePage title='Manage Elasticsearch'>
                    <ClusterOverview />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch/console',
                element: (
                  <RoutablePage title='Deployment console'>
                    <APIConsole />
                  </RoutablePage>
                ),
              },
              {
                path: 'logs-metrics',
                element: (
                  <RoutablePage title='Logs and metrics'>
                    <LogsMetrics />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch/snapshots',
                element: (
                  <RoutablePage title='Deployment snapshots'>
                    <ClusterSnapshots />
                  </RoutablePage>
                ),
              },
              {
                path: 'elasticsearch/snapshots/:snapshotName',
                element: (
                  <RoutablePage title='Snapshot'>
                    <SnapshotDetails />
                  </RoutablePage>
                ),
              },
              {
                path: 'metrics',
                element: (
                  <RoutablePage title='Performance'>
                    <PerformanceMetrics />
                  </RoutablePage>
                ),
              },
              {
                path: 'security',
                element: (
                  <RoutablePage title='Security'>
                    <DeploymentSecurity />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/create',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <CreateDeploymentTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/accounts/:trustRelationshipId',
                element: (
                  <RoutablePage title='Manage trust'>
                    <ManageAccountTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/accounts',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <ManageAccountTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/direct/:trustRelationshipId',
                element: (
                  <RoutablePage title='Manage trust'>
                    <ManageDirectTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'security/trust/direct',
                element: (
                  <RoutablePage title='Add trusted environment'>
                    <ManageDirectTrustRelationship />
                  </RoutablePage>
                ),
              },
              {
                path: 'migrate-to-index-lifecycle-management',
                element: (
                  <RoutablePage title='Migrate to Index Lifecycle Management'>
                    <IlmMigration />
                  </RoutablePage>
                ),
              },

              ...getSupportedSliderInstanceTypesWithoutEs().map((sliderInstanceType) => ({
                path: sliderInstanceType,
                element: (
                  <RoutablePage
                    title={formatMessage(messages.sliderManagePageTitle, {
                      name: formatMessage(getSliderPrettyName({ sliderInstanceType })),
                    })}
                  >
                    <StackDeploymentSlider sliderInstanceType={sliderInstanceType} />
                  </RoutablePage>
                ),
              })),
              {
                path: 'activity',
                children: [
                  {
                    index: true,
                    element: (
                      <RoutablePage title='Deployment Activity'>
                        <StackDeploymentActivity />
                      </RoutablePage>
                    ),
                  },
                  ...getSupportedSliderInstanceTypes().map((sliderInstanceType) => ({
                    path: sliderInstanceType,
                    element: (
                      <RoutablePage
                        title={formatMessage(messages.deploymentActivityPageSlider, {
                          name: formatMessage(getSliderPrettyName({ sliderInstanceType })),
                        })}
                      >
                        <StackDeploymentActivity sliderInstanceType={sliderInstanceType} />
                      </RoutablePage>
                    ),
                  })),
                ],
              },
            ],
          },
        ],
      },
    ]
  }
}
